import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ClickOutsideModule } from 'ng-click-outside';
import { ScrollingModule } from '@angular/cdk/scrolling';

import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { BadgeModule } from 'primeng/badge';

import { SocialCoreModule } from '../Core/social-core.module';
import { SocialRoutingModule } from '../SocialRouter/social-routing.module';
import { LocalizationModule } from '../../Core/Localization/localization.module';
import { CommonFormsModule } from '../../Common/Forms/forms.module';
import { MiscModule } from '../../Common/Misc/misc.module';
import { ChartsModule } from '../../Common/Charts/charts.module';
import { CommonDialogsModule } from '../../Common/Dialogs/common-dialogs.module';
import { LayoutModule } from '../../Common/Layout/layout.module';
import { FilterModule } from '../../Common/Filter/filter.module';
import { CoreDialogsModule } from '../../Core/Dialogs/dialogs.module';
import { CommonBasicModule } from '../../Common/Basic/basic.module';
import { MonitorSharedModule } from '../../Monitor/Shared/monitoring-shared.module';
import { MentionsModule } from '../../Common/Mentions/mentions.module';
import { PageBuilderModule } from '../../Common/PageBuilder/page-builder.module';
import { ReportsModule } from '../../Common/Reports/reports.module';
import { SocialHypeService } from '../Core/services/social-hype.service';
import { TagsModule } from '../../Common/Tags/tags.module';
import { ThemeComponent } from './components/Theme/theme.component';
import { ThemeManagementComponent } from './components/ThemeManagement/theme-management.component';
import { ThemeAnalyticsComponent } from './components/ThemeAnalytics/theme-analytics.component';
import { ThemePostFeedComponent } from './components/ThemePostFeed/theme-post-feed.component';
import { ThemeSentimentAnalyticsComponent } from './components/ThemeAnalytics/ThemeSentimentAnalytics/theme-sentiment-analytics.component';
import { ThemeAnalyticsSummaryComponent } from './components/ThemeAnalytics/ThemeAnalyticsSummary/theme-analytics-summary.component';
import { ThemeEngagementAnalyticsComponent } from './components/ThemeAnalytics/ThemeEngagementAnalytics/theme-engagement-analytics.component';
import { AgentWidgetComponent } from './components/AgentWidget/agent-widget.component';
import { ArchiveSearchWidgetComponent } from './components/ArchiveSearchWidget/archive-search-widget.component';
import { EngagementFilterComponent } from '../../Common/Filter/components/EngagementFilter/engagement-filter.component';
import { SocialFilterInputComponent } from './components/SocialFilterInput/social-filter-input.component';
import { ThemeCardComponent } from './components/ThemeManagement/ThemeCard/theme-card.component';
import { ThemeAnalyticsTrendsComponent } from './components/ThemeAnalytics/ThemeAnalyticsTrends/theme-analytics-trends.component';
import { AgentFilterComponent } from './components/AgentFilter/agent-filter.component';
import { SocialTopicStatusSwitchComponent } from './components/SocialStatusSwitch/social-topic-status-switch.component';
import { ThemeAnalyticsChartSeriesService } from './components/ThemeAnalytics/theme-analytics-chart-series.service';
import { ThemeAnalyticsChartOptionsService } from './components/ThemeAnalytics/theme-analytics-chart-options.service';
import { ContactService } from '../../Common/TargetingShared/services/contact-service';
import { QuotaUsagePanelComponent } from './components/QuotaUsagePanel/quota-usage-panel.component';
import { SocialSeriesService } from './services/social-series.service';
import { SocialChartDetailsDialogService } from './services/social-chart-details-dialog.service';
import { QuotaExceededBannerComponent } from './components/QuotaExceededBanner/quota-exceeded-banner.component';
import { SocialTableComponent } from './components/SocialTable/social-table.component';
import { ManageSocialTopicsDialogComponent } from './components/ManageSocialTopicsDialog/manage-social-topics-dialog.component';
import { AddAgentsToThemeDialogComponent } from './components/AddAgentsToThemeDialog/add-agents-to-theme-dialog.component';
import { SocialBulkActionsDialogComponent } from './components/SocialBulkActionsDialog/social-bulk-actions-dialog.component';
import { SocialPostSaveToolComponent } from './components/social-post-save-tool/social-post-save-tool.component';
import { SocialChartExportService } from './services/social-chart-export.service';
import { SocialArchiveSearchService } from './services/social-archive-search.service';
import { SocialSearchDetailComponent } from './components/SocialSearchDetail/social-search-detail.component';
import { ThemeSidebarComponent } from './components/ThemeSidebar/theme-sidebar.component';
import { UsersModule } from '../../Core/Users/users.module';
import { TakeOverModule } from '../../Common/TakeOver/take-over.module';
import { AgentSummaryComponent } from './components/AgentSummary/agent-summary.component';
import { AgentSearchSettingsComponent } from './components/AgentSearchSettings/agent-search-settings.component';
import { SliderModule } from 'primeng/slider';
import { SocialTopicReplayDialogComponent } from './components/SocialTopicReplayDialog/social-topic-replay-dialog.component';
import { AgentHistoryComponent } from './components/AgentHistory/agent-history.component';
import { SocialSharedModule } from '../Shared/social-shared.module';
import { HandlesTextInputChipsComponent } from './components/HandlesTextInputChips/handles-text-input-chips.component';

export const socialIntelligenceImports = [
    SocialCoreModule,
    SocialRoutingModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SelectModule,
    TooltipModule,
    LocalizationModule,
    AutoCompleteModule,
    CommonFormsModule,
    MiscModule,
    ChartsModule,
    CommonDialogsModule,
    InputTextModule,
    LayoutModule,
    FilterModule,
    CoreDialogsModule,
    CommonBasicModule,
    // @TODO: Make this common - agpr-monitoring-keywords
    MonitorSharedModule,
    MentionsModule,
    PageBuilderModule,
    TagsModule,
    ToggleSwitchModule,
    ClickOutsideModule,
    ReportsModule,
    ButtonModule,
    BadgeModule,
    UsersModule,
    TakeOverModule,
    ScrollingModule,
    SliderModule,
    SocialSharedModule
];

export const socialIntelligenceDeclarations = [
    ThemeComponent,
    ThemeSidebarComponent,
    ThemeManagementComponent,
    ThemeCardComponent,
    ThemeAnalyticsComponent,
    ThemeAnalyticsSummaryComponent,
    ThemeSentimentAnalyticsComponent,
    ThemeAnalyticsTrendsComponent,
    ThemePostFeedComponent,
    AgentWidgetComponent,
    ArchiveSearchWidgetComponent,
    SocialFilterInputComponent,
    EngagementFilterComponent,
    QuotaUsagePanelComponent,
    ThemeEngagementAnalyticsComponent,
    AgentFilterComponent,
    SocialTopicStatusSwitchComponent,
    QuotaExceededBannerComponent,
    ManageSocialTopicsDialogComponent,
    SocialTableComponent,
    AddAgentsToThemeDialogComponent,
    SocialBulkActionsDialogComponent,
    SocialPostSaveToolComponent,
    SocialSearchDetailComponent,
    AgentSummaryComponent,
    AgentSearchSettingsComponent,
    SocialTopicReplayDialogComponent,
    AgentHistoryComponent,
    HandlesTextInputChipsComponent
];

@NgModule({
    imports: [
        SocialCoreModule,
        SocialRoutingModule,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        SelectModule,
        TooltipModule,
        LocalizationModule,
        AutoCompleteModule,
        CommonFormsModule,
        MiscModule,
        ChartsModule,
        CommonDialogsModule,
        ChartsModule,
        InputTextModule,
        LayoutModule,
        FilterModule,
        CoreDialogsModule,
        CommonBasicModule,
        // @TODO: Make this common - agpr-monitoring-keywords
        MonitorSharedModule,
        MentionsModule,
        PageBuilderModule,
        TagsModule,
        ToggleSwitchModule,
        ClickOutsideModule,
        ReportsModule,
        ButtonModule,
        BadgeModule,
        UsersModule,
        TakeOverModule,
        ScrollingModule,
        SliderModule,
        SocialSharedModule
    ],
    exports: [
        ThemeComponent,
        ThemeSidebarComponent,
        ThemeManagementComponent,
        SocialTopicStatusSwitchComponent,
        EngagementFilterComponent
    ],
    declarations: [
        ...socialIntelligenceDeclarations
    ],
    providers: [
        ThemeAnalyticsChartSeriesService,
        ThemeAnalyticsChartOptionsService,
        ContactService,
        SocialSeriesService,
        {
            provide: 'SocialSeriesService',
            useExisting: SocialSeriesService
        },
        ThemePostFeedComponent,
        SocialChartExportService,
        SocialChartDetailsDialogService,
        SocialHypeService,
        SocialChartDetailsDialogService,
        SocialArchiveSearchService
    ]
})
export class IntelligenceModule { }
